import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { IsLoading } from "../../common/components/AppState/IsLoading";
import type { BadgeProps } from "../../common/components/Badge/Badge";
import Badge from "../../common/components/Badge/Badge";
import Icon from "../../common/components/Icon/Icon";
import PlatformIcon from "../../common/components/Icon/PlatformIcon/PlatformIcon";
import PlaceholderWithIcon from "../../common/components/PlaceholderWithIcon/PlaceholderWithIcon";
import { Tooltip } from "../../common/components/Tooltip/Tooltip";
import {
  fetchCurrentProjectGroupBuilds,
  retryPublication,
  getBuilds,
} from "../../features/builds/state/buildsSlice";
import NewPublicationButton from "../../features/projectGroupPublication/components/NewPublicationButton";
import { getSelectedProjectGroup } from "../../features/projectGroups/state/projectGroupsSlice";
import type { ILanguage } from "../../model/model";
import { setAlert } from "../../features/alert/state/alertsSlice";
import { dateConfig } from "src/common/util/DateConfig";
import { getCurrentUser } from "src/features/profile/state/profileSlice";
import { languageConfig } from "src/common/util/langConfig";
import TableComp from "src/common/components/Table/Table";
import DeviceConnectionBar from "src/features/deviceInstaller/components/DeviceConnectionBar";
import InstallOnDeviceButton from "src/features/deviceInstaller/components/InstallOnDeviceButton";
import InstallOnComputerButton from "src/features/deviceInstaller/components/InstallOnComputerButton";
import {
  isApkPlatform,
  isDesktopPlatform,
} from "src/features/deviceInstaller/utils/platformCompat";
import BuildShare from "src/features/builds/components/BuildShare";
import ApkDownloadButton from "src/features/builds/components/ApkDownloadButton";

const SHAREABLE_PLATFORMS = [
  "Android",
  "OculusQuest",
  "MetaQuest",
  "PicoNeo",
  "Windows10",
  "MacOS",
  "WebGLApp",
  "WebGL App",
];
function isShareablePlatform(platform: string): boolean {
  return SHAREABLE_PLATFORMS.includes(platform);
}

export const PROJECT_STATUS_COMPONENT_ID = "projectStatusComponent";
export const PROJECT_GROUP_BUILD_LIST_COMPONENT_ID = "projectGroupBuildListComponent";

export interface ISelectedPlatform {
  platform: string;
}

export const PROJECT_GROUP_PUBLISH_COMPONENT_ID = "projectGroupPublishTableId";

const MAX_VISIBLE_MODULES = 2;

function ModuleChips({
  projects,
  buildId,
  status,
  activeProjectIds,
}: {
  projects: any[] | undefined;
  buildId: number;
  status: string;
  activeProjectIds: Set<number>;
}) {
  const badgeRef = useRef<HTMLDivElement>(null);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null);

  if (!projects || projects.length === 0) {
    return <span className="text-gray-400 italic">No modules</span>;
  }

  const validProjects = projects.filter((p: any) => p.project);
  const errorProjects = projects.filter((p: any) => p?.errorMessages?.error);
  const visible = validProjects.slice(0, MAX_VISIBLE_MODULES);
  const hiddenCount = validProjects.length - visible.length + errorProjects.length;
  const hidden = [...validProjects.slice(MAX_VISIBLE_MODULES), ...errorProjects];

  const isDeleted = (p: any) => !activeProjectIds.has(Number(p.project.id));

  const showTooltip = () => {
    if (!badgeRef.current) return;
    const rect = badgeRef.current.getBoundingClientRect();
    setTooltipPos({ top: rect.top - 8, left: rect.left + rect.width / 2 });
  };

  const hideTooltip = () => setTooltipPos(null);

  return (
    <div className="flex items-center flex-wrap gap-1">
      {visible.map((p: any) => {
        const deleted = isDeleted(p);
        return (
          <div
            className={`px-2 py-0.5 rounded-md text-xs truncate max-w-[120px] border ${
              deleted
                ? "bg-gray-50 text-gray-400 border-gray-200 line-through"
                : "bg-emerald-50 text-emerald-700 border-emerald-200"
            }`}
            key={`${status}-mod-${buildId}-${p.project.id}`}
            title={p.project.name}
          >
            {p.project.name}
          </div>
        );
      })}
      {hiddenCount > 0 && (
        <div ref={badgeRef} onMouseEnter={showTooltip} onMouseLeave={hideTooltip}>
          <div className="bg-gray-100 text-gray-500 border border-gray-200 px-2 py-0.5 rounded-md text-xs cursor-default">
            +{hiddenCount}
          </div>
          {tooltipPos &&
            createPortal(
              <div
                className="fixed z-[9999] flex flex-col items-center pointer-events-none"
                style={{
                  top: tooltipPos.top,
                  left: tooltipPos.left,
                  transform: "translate(-50%, -100%)",
                }}
              >
                <div className="bg-gray-700 text-white text-xs rounded-md shadow-lg p-2 whitespace-nowrap">
                  {hidden.map((p: any, i) => {
                    const name = p?.errorMessages?.error ? "Error" : p.project.name;
                    const deleted = p.project && isDeleted(p);
                    return (
                      <div
                        key={i}
                        className={`py-0.5 ${deleted ? "line-through text-gray-400" : ""}`}
                      >
                        {name}
                      </div>
                    );
                  })}
                </div>
                <div className="w-2 h-2 bg-gray-700 rotate-45 -mt-1" />
              </div>,
              document.body,
            )}
        </div>
      )}
    </div>
  );
}

const ProjectGroupPublish = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentProjectGroup = useAppSelector(getSelectedProjectGroup);
  const builds = useAppSelector(getBuilds);
  const currentUser = useAppSelector(getCurrentUser);

  const currentProjectGroupBuilds = builds
    ? builds.filter((elt) => Number(elt.project_group?.id) === Number(currentProjectGroup.id))
    : [];

  const activeProjectIds = useMemo(() => {
    const ids = new Set<number>();
    for (const pge of currentProjectGroup.project_group_elements ?? []) {
      if (!pge.project?.is_deleted) {
        ids.add(Number(pge.project.id));
      }
    }
    return ids;
  }, [currentProjectGroup.project_group_elements]);

  const lastVersionNumber =
    currentProjectGroupBuilds && currentProjectGroupBuilds.length > 0
      ? [...currentProjectGroupBuilds].sort((a, b) => Number(b.version) - Number(a.version))[0]
          .version
      : -1;

  const noAccessRows = currentProjectGroupBuilds.filter((b) => b.version < lastVersionNumber);
  const currentRows = currentProjectGroupBuilds.filter(
    (b) => b.status === "ChangesNeeded" || b.status === "PublishWaiting",
  );
  const publishedRows = currentProjectGroupBuilds.filter(
    (b) => b.status === "Published" && b.version >= lastVersionNumber,
  );
  const errorRows = currentProjectGroupBuilds.filter((b) => b.status === "ErrorWhileBuilding");

  const preferredLanguage = languageConfig((currentUser?.preferred_language as ILanguage)?.name);

  const [isDarkMode, setIsDarkMode] = useState(false);

  const onSelectMode = (mode: "dark" | "light") => {
    if (mode === "dark") {
      if (!isDarkMode) setIsDarkMode(true);
    } else {
      if (isDarkMode) setIsDarkMode(false);
    }
  };

  useEffect(() => {
    // Check to see if Media-Queries are supported
    if (window.matchMedia) {
      window
        .matchMedia("(prefers-color-scheme: dark)")
        .addEventListener("change", (e) => onSelectMode(e.matches ? "dark" : "light"));

      if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
        if (!isDarkMode) setIsDarkMode(true);
      }
    }
  }, []);

  const fetchBuilds = () => {
    void dispatch(
      fetchCurrentProjectGroupBuilds({
        projectGroupId: currentProjectGroup.id,
        componentId: PROJECT_GROUP_PUBLISH_COMPONENT_ID,
      }),
    );
  };

  useEffect(() => {
    fetchBuilds();
  }, [currentProjectGroup]);

  const getBadgeParams = ({ status }: { status: string }): BadgeProps => {
    switch (status) {
      case "Published":
        return {
          color: "bg-green-500",
          textColor: "text-green-100",
          text: t("general.published"),
        };
      case "PublishWaiting":
        return {
          color: "bg-blue-500",
          textColor: "text-blue-100",
          text: t("pages.projectGroup.publish.buildStatus.inProgress"),
        };
      case "ChangesNeeded":
        return {
          color: "bg-red-500",
          textColor: "text-red-100",
          text: t("pages.projectGroup.publish.buildStatus.requestChanges"),
        };
      case "ErrorWhileBuilding":
        return {
          color: "bg-red-500",
          textColor: "text-red-100",
          text: t("pages.projectGroup.publish.buildStatus.errorWhileBuilding"),
        };
      case "NoAccess":
      default:
        return {
          color: "bg-gray-500",
          textColor: "text-gray-100",
          text: t("pages.projectGroup.publish.buildStatus.noAccess"),
        };
    }
  };

  const retryBuild = ({ buildId }: { buildId: number }) => {
    dispatch(retryPublication({ buildId }))
      .then(() => {
        fetchBuilds();
        dispatch(setAlert({ type: "success", msg: t("alerts.retryPublicationSuccess") }));
      })
      .catch(() => {
        dispatch(setAlert({ type: "danger", msg: t("alerts.retryPublicationFail") }));
      });
  };

  const colHeaders = (status: any) => {
    const commonHeaders = [
      t("pages.projectGroup.publish.buildsTable.version"),
      t("pages.projectGroup.publish.buildsTable.status"),
      t(
        status !== "PublishWaiting" && status !== "ErrorWhileBuilding"
          ? "pages.projectGroup.publish.buildsTable.publishDate"
          : "pages.projectGroup.publish.buildsTable.requestDate",
      ),
      t("pages.projectGroup.publish.buildsTable.modules"),
      t("pages.projectGroup.publish.buildsTable.platform"),
    ];

    if (status === "Published") {
      commonHeaders.push(t("general.download"));
      commonHeaders.push(t("pages.projectGroup.publish.buildsTable.action"));
    }
    if (status === "ErrorWhileBuilding") {
      commonHeaders.push(t("pages.projectGroup.publish.buildsTable.retry"));
    }
    return commonHeaders;
  };

  const rowsPublishWaiting = (data: any, status: any) => {
    return data.map((elt: any) => {
      return {
        check: "",
        version: `V${elt.version}`,
        status: (
          <Badge
            {...getBadgeParams({
              status: status !== "NoAccess" ? elt.status : "NoAccess",
            })}
            className="p-2"
          />
        ),
        publishDate: new Date(
          (status !== "PublishWaiting" && status !== "ErrorWhileBuilding"
            ? elt.publishing_date_ts
            : elt.request_date_ts) * 1000,
        ).toLocaleString(dateConfig(preferredLanguage)),
        modules: (
          <ModuleChips
            projects={elt.project_group_snapshot_sorted_by_order?.published_projects}
            buildId={elt.id}
            status={status}
            activeProjectIds={activeProjectIds}
          />
        ),
        platform: (
          <div className="flex h-8 p-1 pl-1">
            <PlatformIcon
              platform={elt.target_platform}
              scormVersion={elt?.scorm_version}
              iconProps={{ fill: "black" }}
              inTable
            />
          </div>
        ),
      };
    });
  };
  // refacto for build dowload hidde here
  const rowsPublished = (data: any, status: any) => {
    return data.map((elt: any) => {
      return {
        check: "",
        version: `V${elt.version}`,
        status: (
          <Badge
            {...getBadgeParams({
              status: status !== "NoAccess" ? elt.status : "NoAccess",
            })}
            className="p-2"
          />
        ),
        publishDate: new Date(
          (status !== "PublishWaiting" && status !== "ErrorWhileBuilding"
            ? elt.publishing_date_ts
            : elt.request_date_ts) * 1000,
        ).toLocaleString(dateConfig(preferredLanguage)),
        modules: (
          <ModuleChips
            projects={elt.project_group_snapshot_sorted_by_order?.published_projects}
            buildId={elt.id}
            status={status}
            activeProjectIds={activeProjectIds}
          />
        ),
        platform: (
          <div className="flex h-8 p-1 pl-1">
            <PlatformIcon
              platform={elt.target_platform}
              iconProps={{ fill: "black" }}
              scormVersion={elt?.scorm_version}
              inTable
            />
          </div>
        ),

        download:
          status === "Published" ? (
            <div className="flex ml-7 items-center">
              {elt.executable_path ? (
                isApkPlatform(elt.target_platform) ? (
                  <ApkDownloadButton
                    url={elt.s3_url}
                    fileName={elt.name || `build_${elt.id}.apk`}
                  />
                ) : (
                  <button>
                    <a href={elt.s3_url}>
                      <Icon
                        icon={"DownloadIcon"}
                        className={""}
                        width={24}
                        stroke="rgb(0 200 137)"
                      />
                    </a>
                  </button>
                )
              ) : (
                <Tooltip
                  message={t("pages.projectGroup.publish.brokenLinkBuildTooltip")}
                  classNameChildren="right-0"
                >
                  <Icon
                    icon={"ExclamationIcon"}
                    className={"stroke-yellow-400 dark:stroke-yellow-200"}
                    width={24}
                  />
                </Tooltip>
              )}
            </div>
          ) : status === "ErrorWhileBuilding" ? (
            <td scope="row" className="px-6">
              <div className="flex justify-center">
                <button onClick={() => retryBuild({ buildId: elt.id })}>
                  <Icon icon={"RefreshIcon"} className={""} width={24} stroke="rgb(0 200 137)" />
                </button>
              </div>
            </td>
          ) : null,

        ...(status === "Published"
          ? {
              action: (
                <div className="flex items-center gap-2">
                  {(elt.executable_path || elt.public_s3_url) &&
                    isShareablePlatform(elt.target_platform) && <BuildShare build={elt} />}
                  {isApkPlatform(elt.target_platform) && elt.executable_path ? (
                    <InstallOnDeviceButton build={elt} />
                  ) : isDesktopPlatform(elt.target_platform) && elt.s3_url ? (
                    <InstallOnComputerButton build={elt} />
                  ) : null}
                </div>
              ),
            }
          : {}),
      };
    });
  };

  const getGridRowClass = () => {
    let nbRows = 0;
    currentRows.length > 0 ? nbRows++ : null;
    publishedRows.length > 0 ? nbRows++ : null;
    noAccessRows.length > 0 ? nbRows++ : null;
    errorRows.length > 0 ? nbRows++ : null;

    switch (nbRows) {
      case 1:
        return "grid-rows-1";
      case 2:
        return "grid-rows-2";
      case 3:
        return "grid-rows-3";
      case 4:
        return "grid-rows-4";
      default:
        return "";
    }
  };

  return (
    <>
      <IsLoading componentId={PROJECT_GROUP_PUBLISH_COMPONENT_ID} showSuccess={false}>
        <div className="h-full w-full flex flex-col" style={{ height: "calc(100% - 10rem)" }}>
          {currentProjectGroupBuilds.length > 0 ? (
            <div className="h-full w-full">
              <div className="flex items-center justify-between mx-4 mt-2 mb-2 min-h-[2.5rem]">
                <div className="flex items-center min-w-0">
                  {currentProjectGroupBuilds.some(
                    (b) => b.status === "Published" && isApkPlatform(b.target_platform),
                  ) && <DeviceConnectionBar />}
                </div>
                <div className="shrink-0 ml-4">
                  <NewPublicationButton />
                </div>
              </div>
              <div className={`grid ${getGridRowClass()}`} style={{ height: "calc(100% - 3rem)" }}>
                {currentRows.length > 0 && (
                  <div className="m-4 flex flex-col">
                    <h1 className="font-medium text-xl">
                      {t("pages.projectGroup.publish.inProgressPublish")}
                    </h1>

                    <div className="overflow-y-auto">
                      {/* {getPublishingTable({ data: currentRows, status: "PublishWaiting" })} */}
                      <TableComp
                        isCursorDesativated={true}
                        filterActivated={false}
                        rows={rowsPublishWaiting(currentRows, "PublishWaiting")}
                        colHeaders={colHeaders("PublishWaiting")}
                        className="h-full w-full overflow-x-hidden the-table-comp"
                      />
                    </div>
                  </div>
                )}

                {errorRows.length > 0 && (
                  <div className="m-4 flex flex-col">
                    <h1 className="font-medium text-xl">
                      {t("pages.projectGroup.publish.failedPublish")}
                    </h1>
                    <div className="overflow-y-auto">
                      <TableComp
                        isCursorDesativated={true}
                        filterActivated={false}
                        rows={rowsPublished(errorRows, "ErrorWhileBuilding")}
                        colHeaders={colHeaders("ErrorWhileBuilding")}
                        className="h-full w-full overflow-x-hidden the-table-comp"
                      />
                    </div>
                  </div>
                )}

                {publishedRows.length > 0 && (
                  <div className="m-4 flex flex-col">
                    <h1 className="font-medium text-xl">
                      {t("pages.projectGroup.publish.finishedPublish")}
                    </h1>
                    <div className="overflow-y-auto">
                      <TableComp
                        isCursorDesativated={true}
                        filterActivated={false}
                        rows={rowsPublished(publishedRows, "Published")}
                        colHeaders={colHeaders("Published")}
                        className="h-full w-full overflow-x-hidden the-table-comp"
                      />
                    </div>
                  </div>
                )}

                {noAccessRows.length > 0 && (
                  <div className="m-4 flex flex-col">
                    <h1 className="font-medium text-xl">
                      {t("pages.projectGroup.publish.noAccessPublish")}
                    </h1>

                    <div className="overflow-y-auto">
                      <TableComp
                        isCursorDesativated={true}
                        filterActivated={false}
                        rows={rowsPublishWaiting(noAccessRows, "NoAccess")}
                        colHeaders={colHeaders("NoAccess")}
                        className="h-full w-full overflow-x-hidden the-table-comp"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="w-full h-full flex justify-center items-center">
              <div className="h-72 space-y-11">
                <PlaceholderWithIcon
                  title={t("pages.projectGroup.publish.noBuilds")}
                  text={t("pages.projectGroup.publish.noBuildsSubtitle")}
                  heroIcon="InformationCircleIcon"
                />
                <div className="flex justify-center items-center">
                  <NewPublicationButton />
                </div>
              </div>
            </div>
          )}
        </div>
      </IsLoading>
    </>
  );
};

export default ProjectGroupPublish;
